import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../utils/axiosInstance';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Dropdown } from '@/components/ui/dropdown';
import { BASE_URL } from '@/config/constants';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Cropper } from '@/components/ui/image-crop';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/components/ui/pagination';
import { PageLoader } from '@/components/ui/page-loader';
import { usePagination } from '@/hooks/usePagination';
import { Upload, RefreshCw, Trash2, Image as ImageIcon } from 'lucide-react';
import { usePlan } from '@/context/PlanContext';
import { PlanLimitBadge, GracePeriodBanner, PlanItemBadge } from '@/components/PlanComponents';
import SectionTextSettingsCard from '@/components/SectionTextSettingsCard';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

interface ImageItem {
  _id: string;
  image: string;
  title: string;
  category: string;
  description: string;
  order: number;
  isActive?: boolean;
}

interface Category {
  _id: string;
  name: string;
  parentCategory?: {
    _id: string;
    name: string;
  } | null;
}

interface SortableTableRowProps {
  item: ImageItem;
  index: number;
  categories: Category[];
  onEdit: (item: ImageItem) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string) => void;
  onCategoryChange: (id: string, categoryId: string) => void;
  togglingId: string | null;
}

const SortableTableRow: React.FC<SortableTableRowProps> = ({ item, index, categories, onEdit, onDelete, onToggleActive, onCategoryChange, togglingId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item._id });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.05)' : undefined,
  };

  return (
    <TableRow ref={setNodeRef} style={style} className="hover:bg-muted/50 transition-colors">
      <TableCell className="w-10 select-none cursor-grab">
        <div className="flex items-center gap-2" {...attributes} {...listeners}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
            <circle cx="9" cy="12" r="1"/><circle cx="9" cy="5" r="1"/><circle cx="9" cy="19" r="1"/>
            <circle cx="15" cy="12" r="1"/><circle cx="15" cy="5" r="1"/><circle cx="15" cy="19" r="1"/>
          </svg>
          <span className="w-4 text-muted-foreground font-medium">{index + 1}</span>
        </div>
      </TableCell>
      <TableCell className="text-left">{item.image ? <img src={item.image} alt="img" className="w-16 h-16 object-cover rounded" /> : <span className="text-sm text-muted-foreground">-</span>}</TableCell>
      <TableCell className="font-medium text-foreground">{item.title}</TableCell>
      <TableCell>
        <div className="min-w-[120px]">
          <Dropdown
            value={item.category}
            options={[
              { value: '', label: 'Select Category' },
              ...categories.map(c => ({
                value: c._id,
                label: c.parentCategory ? `${c.parentCategory.name} › ${c.name}` : c.name,
              })),
            ]}
            onValueChange={(val) => {
              if (val) onCategoryChange(item._id, val as string);
            }}
            triggerClassName="!w-auto !py-1 !px-2 !text-xs"
            panelClassName="!min-w-[180px]"
          />
        </div>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{item.description}</TableCell>
      <TableCell className="text-left">
        <div className="flex items-center space-x-2">
          <Switch
            checked={item.isActive || false}
            onCheckedChange={() => onToggleActive(item._id)}
            disabled={togglingId === item._id}
            aria-label="Toggle image active state"
          />
          <span className={item.isActive ? 'status-label-active' : 'status-label-inactive'}>
            {item.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <PlanItemBadge resourceType="images" index={index} />
      </TableCell>
      <TableCell className="text-left">
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="icon-sm" 
            onClick={() => onEdit(item)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </Button>
          <Button 
            variant="destructive" 
            size="icon-sm" 
            onClick={() => onDelete(item._id)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const emptyImage = {
  image: '',
  title: '',
  category: '',
  description: '',
  order: 0,
};
interface ImageManagerProps {
  domain: string;
}

const ImageManager: React.FC<ImageManagerProps> = ({ domain }) => {
  const { canAdd, openUpgradeModal, adjustUsage } = usePlan();
  const location = useLocation();
  const navigate = useNavigate();
  const [images, setImages] = useState<ImageItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [form, setForm] = useState<Omit<ImageItem, '_id'>>(emptyImage);
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadStep, setUploadStep] = useState<'idle' | 'selecting' | 'cropping'>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedParent, setSelectedParent] = useState('');

  const cropperRef = useRef<{ applyCrop: () => Promise<void> }>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = images.findIndex((item) => item._id === active.id);
      const newIndex = images.findIndex((item) => item._id === over.id);
      
      const newItems = arrayMove(images, oldIndex, newIndex);
      setImages(newItems);
      
      try {
        await axios.post(`${BASE_URL}/api/images/reorder`, { items: newItems });
        toast.success('Image order updated successfully!');
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Failed to update image order');
        // Revert the order if the API call fails
        fetchImages();
      }
    }
  };

  // Helper function to get auth headers (x-domain is added by axiosInstance)
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      return null;
    }
    // Return empty object - headers will be added by axiosInstance interceptor
    return {};
  };

  const fetchImages = () => {
    setLoading(true);
    const headers = getAuthHeaders();

    const config = headers ? { headers } : {};

    axios.get(BASE_URL + '/api/images', config)
      .then(res => setImages(res.data))
      .catch(err => {
        console.error('Failed to fetch images:', err);
        if (err.response?.status === 401) {
          setError('Authentication failed. Please log in again.');
        }
      })
      .finally(() => setLoading(false));
  };

  const fetchCategories = () => {
    axios.get(BASE_URL + '/api/category', { params: { type: 'image' } })
      .then(res => setCategories(res.data));
  };

  const resetUploadState = () => {
    setUploadStep('idle');
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  const openCropperForExisting = () => {
    if (form.image) {
      fetch(form.image)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], 'image.png', { type: blob.type });
          setSelectedFile(file);
          setUploadStep('cropping');
        })
        .catch(() => {
          toast.error('Failed to load image for editing');
        });
    }
  };

  useEffect(() => {
    fetchImages();
    fetchCategories();
  }, [domain]);

  // Auto-open the "Add Image" dialog when navigated here from the dashboard
  // card (state.openAdd). Mirrors the Add button's plan-limit behaviour, then
  // clears the navigation state so it doesn't re-trigger on refresh/back.
  useEffect(() => {
    if (!(location.state as { openAdd?: boolean } | null)?.openAdd) return;
    navigate(location.pathname, { replace: true, state: null });
    if (!canAdd('images')) {
      openUpgradeModal('images');
    } else {
      setAddOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const target = e.target as HTMLSelectElement;
    const { name, value } = target;

    if (name === 'parentCategory') {
      const parentId = value;
      setSelectedParent(parentId);
      const hasSub = categories.some(cat => cat.parentCategory?._id === parentId);
      setForm(prev => ({
        ...prev,
        category: hasSub ? '' : parentId,
      }));
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const parentCategories = categories.filter(cat => !cat.parentCategory);
  const subcategories = categories.filter(cat => cat.parentCategory && cat.parentCategory._id === selectedParent);
  const hasSubcategories = subcategories.length > 0;
  const effectiveCategory = hasSubcategories ? form.category : selectedParent;

  // Clear category error once a category is selected
  useEffect(() => {
    if (effectiveCategory && error === 'Please select a category.') {
      setError('');
    }
  }, [effectiveCategory]);

  // Pagination
  const { startIndex, paginate, paginationProps } = usePagination(images.length);
  const paginatedImages = paginate(images);

  const renderCategoryInputs = () => (
    <div className="space-y-2">
      <Dropdown
        value={selectedParent}
        options={[
          { value: '', label: 'Select Category' },
          ...parentCategories.map(p => ({ value: p._id, label: p.name })),
        ]}
        onValueChange={(val) => {
          const parentId = val as string;
          setSelectedParent(parentId);
          if (!parentId) {
            setForm(prev => ({ ...prev, category: '' }));
          } else {
            const hasSub = categories.some(cat => cat.parentCategory?._id === parentId);
            setForm(prev => ({ ...prev, category: hasSub ? '' : parentId }));
          }
        }}
      />

      {hasSubcategories && (
        <Dropdown
          value={form.category}
          options={[
            { value: '', label: 'Select Subcategory' },
            ...subcategories.map(s => ({ value: s._id, label: s.name })),
          ]}
          onValueChange={(val) => setForm(prev => ({ ...prev, category: val as string }))}
        />
      )}

      {!hasSubcategories && selectedParent && (
        <input type="hidden" name="category" value={selectedParent} />
      )}
    </div>
  );

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validFormats = ['image/png', 'image/jpeg'];
    const maxSize = 2 * 1024 * 1024;
    
    if (!validFormats.includes(file.type)) {
      setError('Only .png and .jpeg files are allowed.');
      toast.error('Only .png and .jpeg files are allowed.');
      return;
    }
    
    if (file.size > maxSize) {
      setError('File size must be less than 2MB.');
      toast.error('File size must be less than 2MB.');
      return;
    }
    
    setError('');
    setSelectedFile(file);
    setUploadStep('cropping');
    // Auto-fill title from filename
    if (!form.title) {
      const name = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
      setForm(prev => ({ ...prev, title: name }));
    }
  };

  const handleCropComplete = async (croppedImage: string) => {
    if (!croppedImage) return;
    
    setUploading(true);
    try {
      const response = await fetch(croppedImage);
      const blob = await response.blob();
      
      const formData = new FormData();
      formData.append('file', blob, 'cropped-image.png');
      
      const res = await axios.post(BASE_URL + '/api/images/upload', formData);
      setForm((prev: Omit<ImageItem, '_id'>) => ({ ...prev, image: res.data.url }));
      resetUploadState();
      const isUpdate = form.image ? true : false;
      toast.success(isUpdate ? 'Image updated successfully!' : 'Image uploaded successfully!');
    } catch (err) {
      setError('Image upload failed.');
      toast.error('Image upload failed.');
    } finally {
      setUploading(false);
    }
  };


  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.image) {
      setError('Please upload an image.');
      return;
    }
    const categoryToSend = hasSubcategories ? form.category : selectedParent;
    if (!categoryToSend) {
      setError('Please select a category.');
      return;
    }

    const headers = getAuthHeaders();
    if (!headers) return;

    setSaving(true);
    try {
      const response = await axios.post(BASE_URL + '/api/images', { ...form, category: categoryToSend }, { headers });
      setAddOpen(false);
      setForm(emptyImage);
      resetUploadState();
      toast.success('Image added successfully!');
      adjustUsage('images', 1);
      if (response.data) {
        setImages(prev => [...prev, response.data]);
      } else {
        fetchImages();
      }
    } catch (err: any) {
      const errorMessage = err.response?.status === 401
        ? 'Authentication failed. Please log in again.'
        : err.response?.data?.message || 'Failed to add image';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item: ImageItem) => {
    setEditId(item._id);
    const cat = categories.find(c => c._id === item.category);

    // Determine if it's a subcategory or top-level category
    if (cat?.parentCategory?._id) {
      setSelectedParent(cat.parentCategory._id);
    } else if (cat?._id) {
      setSelectedParent(cat._id);
    } else {
      setSelectedParent('');
    }

    setForm(item);
    setEditOpen(true);
    setError('');
    openCropperForExisting();
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.image) {
      setError('Please upload an image.');
      return;
    }
    const categoryToSend = hasSubcategories ? form.category : selectedParent;
    if (!categoryToSend) {
      setError('Please select a category.');
      return;
    }

    const headers = getAuthHeaders();
    if (!headers) return;

    setSaving(true);
    const currentEditId = editId;
    try {
      const response = await axios.put(`${BASE_URL}/api/images/${editId}`, { ...form, category: categoryToSend }, { headers });
      setEditOpen(false);
      setEditId(null);
      setForm(emptyImage);
      resetUploadState();
      toast.success('Image updated successfully!');
      if (response.data) {
        setImages(prev => prev.map(image =>
          image._id === currentEditId ? { ...image, ...response.data } : image
        ));
      } else {
        fetchImages();
      }
    } catch (err: any) {
      const errorMessage = err.response?.status === 401
        ? 'Authentication failed. Please log in again.'
        : err.response?.data?.message || 'Failed to update image';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    const headers = getAuthHeaders();
    if (!headers) return;

    const deletedImage = images.find(image => image._id === deleteId);
    // Optimistic removal
    setImages(prev => prev.filter(image => image._id !== deleteId));
    setDeleteId(null);
    try {
      await axios.delete(`${BASE_URL}/api/images/${deleteId}`, { headers });
      toast.success('Image deleted successfully!');
      adjustUsage('images', -1);
    } catch (err: any) {
      // Revert on error
      if (deletedImage) {
        setImages(prev => [...prev, deletedImage].sort((a, b) => a.order - b.order));
      }
      const errorMessage = err.response?.status === 401
        ? 'Authentication failed. Please log in again.'
        : err.response?.data?.message || 'Failed to delete image';
      toast.error(errorMessage);
    }
  };

  const handleToggleActive = async (id: string) => {
    const item = images.find(m => m._id === id);
    const headers = getAuthHeaders();
    if (!item || !headers) return;

    const currentStatus = item.isActive || false;
    // Optimistic update - flip the switch immediately so the click responds
    setImages(prev => prev.map(m => m._id === id ? { ...m, isActive: !currentStatus } : m));
    setTogglingId(id);
    try {
      const response = await axios.patch(`${BASE_URL}/api/images/${id}/toggle-active`, {}, { headers });
      setImages(prev => prev.map(m => m._id === id ? response.data : m));
      toast.success(`Image ${response.data.isActive ? 'shown' : 'hidden'} successfully!`);
    } catch (err: any) {
      // Revert on error
      setImages(prev => prev.map(m => m._id === id ? { ...m, isActive: currentStatus } : m));
      const errorMessage = err.response?.data?.message || 'Failed to toggle image status';
      toast.error(errorMessage);
    } finally {
      setTogglingId(null);
    }
  };

  const handleCategoryChange = async (id: string, categoryId: string) => {
    const item = images.find(m => m._id === id);
    if (!item || item.category === categoryId) return;
    setImages(prev => prev.map(m => m._id === id ? { ...m, category: categoryId } : m));
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.put(`${BASE_URL}/api/images/${id}`, { category: categoryId }, { headers });
      toast.success('Category updated');
    } catch (err: any) {
      setImages(prev => prev.map(m => m._id === id ? { ...m, category: item.category } : m));
      toast.error(err.response?.data?.message || 'Failed to update category');
    }
  };

  return (
    <div>
      <GracePeriodBanner />
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="page-title">Image Gallery Management</h2>
          <PlanLimitBadge resourceType="images" />
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={(e) => {
                if (!canAdd('images')) {
                  e.preventDefault();
                  e.stopPropagation();
                  openUpgradeModal('images');
                }
              }}
            >
              Add Image
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Image</DialogTitle>
              <DialogDescription>
                Upload a new image to your gallery. Accepted formats: PNG, JPEG (max 2MB).
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-5">
              <div className="form-group">
                <Label className="form-label">Gallery Image</Label>
                {uploadStep === 'idle' ? (
                  !form.image ? (
                    <div className="w-full">
                      <Card
                        className="group flex max-h-[200px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                        onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
                          e.preventDefault();
                          e.currentTarget.classList.add('bg-muted/50');
                        }}
                        onDragLeave={(e: React.DragEvent<HTMLDivElement>) => {
                          e.currentTarget.classList.remove('bg-muted/50');
                        }}
                        onDrop={(e: React.DragEvent<HTMLDivElement>) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('bg-muted/50');
                          const files = e.dataTransfer.files;
                          if (files.length > 0) {
                            const event = {
                              target: { files }
                            } as any;
                            handleFileChange(event);
                          }
                        }}
                        onClick={openFileUpload}
                      >
                        <div className="grid space-y-3">
                          <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                            <Upload className="size-5" />
                            <div>
                              Drop image here or{" "}
                              <Button
                                type="button"
                                variant="link"
                                className="text-primary p-0 h-auto font-normal"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openFileUpload();
                                }}
                              >
                                browse
                              </Button>{" "}
                              to upload
                            </div>
                          </div>
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          accept=".png,.jpg,.jpeg"
                          onChange={handleFileChange}
                        />
                        <span className="text-xs text-muted-foreground mt-1 block">
                          Supported: JPG, PNG (max 2 MB)
                        </span>
                      </Card>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Card
                        className="group flex max-h-[400px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={openCropperForExisting}
                      >
                        <div 
                          className="relative max-h-64 w-full sm:max-w-64 rounded-2xl overflow-hidden"
                          style={{
                            backgroundImage: 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb)',
                            backgroundSize: '16px 16px',
                            backgroundPosition: '0 0, 8px 8px',
                            backgroundRepeat: 'repeat'
                          }}
                        >
                          <img 
                            src={form.image} 
                            alt="uploaded" 
                            className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity" 
                            title="Click to edit image"
                          />
                        </div>
                        <div className="flex items-center gap-x-2 text-muted-foreground text-xs">
                          <Upload className="size-4" />
                          <span>Click to edit or re-upload</span>
                        </div>
                      </Card>
                      <div className="flex gap-2">
                        <Button 
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={openFileUpload}
                        >
                          <RefreshCw className="size-3.5" />
                          Change
                        </Button>
                        <Button 
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-danger"
                          onClick={() => setForm((prev: Omit<ImageItem, '_id'>) => ({ ...prev, image: '' }))}
                        >
                          <Trash2 className="size-3.5" />
                          Remove
                        </Button>
                      </div>
                      <input
                        type="file"
                        accept=".png,.jpg,.jpeg"
                        onChange={handleFileChange}
                        ref={fileInputRef}
                        className="hidden"
                      />
                    </div>
                  )
                ) : uploadStep === 'cropping' && selectedFile ? (
                  <div className="space-y-2">
                    <Card className="flex flex-col items-center">
                      <div className="w-full border-b p-3">
                        <p className="text-sm font-medium text-foreground text-center">Adjust crop area - Drag to move, edges to resize</p>
                      </div>
                      <div className="w-full flex justify-center py-3">
                        <Cropper
                          file={selectedFile}
                          aspect={undefined}
                          maxImageSize={2 * 1024 * 1024}
                          keepSelection
                          onCrop={handleCropComplete}
                          onCropRef={cropperRef}
                        />
                      </div>
                    </Card>
                    <div className="flex gap-2">
                      <Button 
                        type="button"
                        onClick={async () => {
                          await cropperRef.current?.applyCrop();
                        }}
                        disabled={uploading}
                        size="sm"
                        loading={uploading}
                        loadingText="Uploading..."
                      >
                        {form.image ? 'Update' : 'Upload'}
                      </Button>
                      <Button 
                        type="button"
                        variant="outline"
                        onClick={() => resetUploadState()}
                        disabled={uploading}
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input name="title" placeholder="Live at Venue Name" value={form.title} onChange={handleFormChange} required />
              </div>
              <div className="space-y-1.5">
                <Label>Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Textarea name="description" placeholder="A short caption for this image" value={form.description} onChange={handleFormChange} />
              </div>
              <div className="form-group">
                <Label className="form-label">Category <span className="text-red-500">*</span></Label>
                {renderCategoryInputs()}
              </div>
              {error && <div className="text-danger">{error}</div>}
              <Button type="submit" loading={saving} loadingText="Adding...">Add</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <SectionTextSettingsCard sectionKey="gallery" />

      <div>
        {loading ? (
          <PageLoader label="Loading images..." />
        ) : images.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <ImageIcon className="w-full h-full" />
            </div>
            <h3 className="empty-state-title">No images found</h3>
            <p className="empty-state-description">Upload your first image to get started</p>
          </div>
        ) : (
          <>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-10">Order</TableHead>
                    <TableHead>Image</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Website</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="text-left">
                  <SortableContext items={paginatedImages.map(item => item._id)} strategy={verticalListSortingStrategy}>
                    {paginatedImages.map((item, idx) => (
                      <SortableTableRow
                        key={item._id}
                        item={item}
                        index={startIndex + idx}
                        categories={categories}
                        onEdit={handleEdit}
                        onDelete={setDeleteId}
                        onToggleActive={handleToggleActive}
                        onCategoryChange={handleCategoryChange}
                        togglingId={togglingId}
                      />
                    ))}
                  </SortableContext>
                </TableBody>
              </Table>
            </DndContext>

            {/* Pagination Controls */}
            <Pagination
              {...paginationProps}
              itemLabel="images"
            />
          </>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Image</DialogTitle>
            <DialogDescription>
              Update the image details. You can also upload a new image to replace the current one.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-5">
            <div className="form-group">
              <Label className="form-label">Gallery Image</Label>
              {uploadStep === 'idle' ? (
                !form.image ? (
                  <div className="w-full">
                    <Card
                      className="group flex max-h-[200px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                      onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
                        e.preventDefault();
                        e.currentTarget.classList.add('bg-muted/50');
                      }}
                      onDragLeave={(e: React.DragEvent<HTMLDivElement>) => {
                        e.currentTarget.classList.remove('bg-muted/50');
                      }}
                      onDrop={(e: React.DragEvent<HTMLDivElement>) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('bg-muted/50');
                        const files = e.dataTransfer.files;
                        if (files.length > 0) {
                          const event = {
                            target: { files }
                          } as any;
                          handleFileChange(event);
                        }
                      }}
                      onClick={openFileUpload}
                    >
                      <div className="grid space-y-3">
                        <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                          <Upload className="size-5" />
                          <div>
                            Drop image here or{" "}
                            <Button
                              type="button"
                              variant="link"
                              className="text-primary p-0 h-auto font-normal"
                              onClick={(e) => {
                                e.stopPropagation();
                                openFileUpload();
                              }}
                            >
                              browse
                            </Button>{" "}
                            to upload
                          </div>
                        </div>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept=".png,.jpg,.jpeg"
                        onChange={handleFileChange}
                      />
                      <span className="text-xs text-muted-foreground mt-1 block">
                        Supported: JPG, PNG (max 2 MB)
                      </span>
                    </Card>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Card
                      className="group flex max-h-[400px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={openCropperForExisting}
                    >
                      <div 
                        className="relative max-h-64 w-full sm:max-w-64 rounded-2xl overflow-hidden"
                        style={{
                          backgroundImage: 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb)',
                          backgroundSize: '16px 16px',
                          backgroundPosition: '0 0, 8px 8px',
                          backgroundRepeat: 'repeat'
                        }}
                      >
                        <img 
                          src={form.image} 
                          alt="uploaded" 
                          className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity" 
                          title="Click to edit image"
                        />
                      </div>
                      <div className="flex items-center gap-x-2 text-muted-foreground text-xs">
                        <Upload className="size-4" />
                        <span>Click to edit or re-upload</span>
                      </div>
                    </Card>
                    <div className="flex gap-2">
                      <Button 
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={openFileUpload}
                      >
                        <RefreshCw className="size-3.5" />
                        Change
                      </Button>
                      <Button 
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-danger"
                        onClick={() => setForm((prev: Omit<ImageItem, '_id'>) => ({ ...prev, image: '' }))}
                      >
                        <Trash2 className="size-3.5" />
                        Remove
                      </Button>
                    </div>
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                      className="hidden"
                    />
                  </div>
                )
              ) : uploadStep === 'cropping' && selectedFile ? (
                <div className="space-y-2">
                  <Card className="flex flex-col items-center">
                    <div className="w-full border-b p-3">
                      <p className="text-sm font-medium text-foreground text-center">Adjust crop area - Drag to move, edges to resize</p>
                    </div>
                    <div className="w-full flex justify-center py-3">
                      <Cropper
                        file={selectedFile}
                        aspect={undefined}
                        maxImageSize={2 * 1024 * 1024}
                        keepSelection
                        onCrop={handleCropComplete}
                        onCropRef={cropperRef}
                      />
                    </div>
                  </Card>
                  <div className="flex gap-2">
                    <Button 
                      type="button"
                      onClick={async () => {
                        await cropperRef.current?.applyCrop();
                      }}
                      disabled={uploading}
                      size="sm"
                      loading={uploading}
                      loadingText="Uploading..."
                    >
                      {form.image ? 'Update' : 'Upload'}
                    </Button>
                    <Button 
                      type="button"
                      variant="outline"
                      onClick={() => resetUploadState()}
                      disabled={uploading}
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input name="title" placeholder="Live at Venue Name" value={form.title} onChange={handleFormChange} required />
            </div>
            <div className="space-y-1.5">
              <Label>Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Textarea name="description" placeholder="A short caption for this image" value={form.description} onChange={handleFormChange} />
            </div>
            <div className="form-group">
              <Label className="form-label">Category <span className="text-red-500">*</span></Label>
              {renderCategoryInputs()}
            </div>
            {error && <div className="text-danger">{error}</div>}
            <Button type="submit" loading={saving} loadingText="Saving...">Save</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
        title="Delete Image"
        description="This action cannot be undone. The image will be permanently removed from your gallery."
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default ImageManager;